/**
 * Corridor Intelligence Layer
 * Transatlantic cocaine corridor visualization: routes, seizures, vessel tracks,
 * waypoints, and heatmap. Provides animation helpers and visibility controls.
 *
 * Architecture:
 *   - 5 sources, 8 layers
 *   - Routes source shared by 3 filtered line layers (northern/central/southern)
 *   - Waypoints source shared by 2 circle layers (solid inner + pulse outer)
 *   - Seizure popups follow the eezLayer.ts hover pattern
 */

import mapboxgl from 'mapbox-gl';
import type { MapPopupField, MapPopupProps } from '@/components/molecules/MapPopup';
import { setMapPopupContent } from '@/utils/map/mapPopup';
import { waitForStyleLoaded } from '../geoJsonLayerHelper';

// ─────────────────────────────────────────────────────────────────────────────
// Direct source/layer helpers — bypass ensureGeoJsonLayer's isStyleLoaded()
// guard which returns false during tile loading on globe view.
// ─────────────────────────────────────────────────────────────────────────────

function addSourceDirect(
  map: mapboxgl.Map,
  sourceId: string,
  data: GeoJSON.FeatureCollection | GeoJSON.Feature,
): void {
  if (map.getSource(sourceId)) return;
  try {
    map.addSource(sourceId, { type: 'geojson', data });
  } catch (err) {
    console.error(`Corridor: failed to add source ${sourceId}`, err);
  }
}

function addLayerDirect(map: mapboxgl.Map, config: mapboxgl.AnyLayer): void {
  if (map.getLayer(config.id)) return;
  try {
    map.addLayer(config);
  } catch (err) {
    console.error(`Corridor: failed to add layer ${config.id}`, err);
  }
}
import {
  fetchCorridorIncidents,
  fetchCorridorRoutes,
  getVesselTrack,
} from '@/services/corridorDataService';
import type { CorridorIncident, CorridorRoute, SeizureMethod } from '@/types/corridor';

// ─────────────────────────────────────────────────────────────────────────────
// Source IDs
// ─────────────────────────────────────────────────────────────────────────────

export const ROUTES_SOURCE = 'corridor-routes-source';
export const VESSEL_TRACK_SOURCE = 'corridor-vessel-track-source';
export const SEIZURES_SOURCE = 'corridor-seizures-source';
export const WAYPOINTS_SOURCE = 'corridor-waypoints-source';
export const HEATMAP_SOURCE = 'corridor-heatmap-source';

// ─────────────────────────────────────────────────────────────────────────────
// Layer IDs
// ─────────────────────────────────────────────────────────────────────────────

export const ROUTE_NORTHERN_LAYER = 'corridor-route-northern';
export const ROUTE_CENTRAL_LAYER = 'corridor-route-central';
export const ROUTE_SOUTHERN_LAYER = 'corridor-route-southern';
export const VESSEL_TRACK_LAYER = 'corridor-vessel-track';
export const SEIZURES_LAYER = 'corridor-seizures';
export const WAYPOINTS_LAYER = 'corridor-waypoints';
export const WAYPOINTS_PULSE_LAYER = 'corridor-waypoints-pulse';
export const HEATMAP_LAYER = 'corridor-heatmap';

export const CORRIDOR_LAYER_IDS = [
  ROUTE_NORTHERN_LAYER,
  ROUTE_CENTRAL_LAYER,
  ROUTE_SOUTHERN_LAYER,
  VESSEL_TRACK_LAYER,
  SEIZURES_LAYER,
  WAYPOINTS_LAYER,
  WAYPOINTS_PULSE_LAYER,
  HEATMAP_LAYER,
];

export const CORRIDOR_SOURCE_IDS = [
  ROUTES_SOURCE,
  VESSEL_TRACK_SOURCE,
  SEIZURES_SOURCE,
  WAYPOINTS_SOURCE,
  HEATMAP_SOURCE,
];

// ─────────────────────────────────────────────────────────────────────────────
// Route colors
// ─────────────────────────────────────────────────────────────────────────────

const ROUTE_COLORS: Record<string, string> = {
  northern: '#FF6B6B',
  central: '#FFD93D',
  southern: '#6BCB77',
};

// ─────────────────────────────────────────────────────────────────────────────
// Seizure method colors & labels
// ─────────────────────────────────────────────────────────────────────────────

const METHOD_COLORS: Record<SeizureMethod, string> = {
  fishing_vessel: '#FF8C42',
  container: '#4ECDC4',
  go_fast: '#FF6B6B',
  narco_sub: '#C44DFF',
  sailboat: '#45B7D1',
  other: '#888888',
};

const METHOD_LABELS: Record<SeizureMethod, string> = {
  fishing_vessel: 'Fishing Vessel',
  container: 'Container Ship',
  go_fast: 'Go-Fast Boat',
  narco_sub: 'Narco Submarine',
  sailboat: 'Sailboat',
  other: 'Other',
};

// ─────────────────────────────────────────────────────────────────────────────
// GeoJSON builders
// ─────────────────────────────────────────────────────────────────────────────

function buildRoutesGeoJson(routes: CorridorRoute[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: routes.map((route) => ({
      type: 'Feature' as const,
      properties: {
        id: route.id,
        name: route.name,
        type: route.type,
        color: ROUTE_COLORS[route.type] ?? '#FFFFFF',
      },
      geometry: route.geojson.geometry,
    })),
  };
}

function buildSeizuresGeoJson(incidents: CorridorIncident[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: incidents.map((inc) => ({
      type: 'Feature' as const,
      properties: {
        id: inc.id,
        operation: inc.operation,
        date: inc.date,
        seizure_kg: inc.seizure_kg,
        method: inc.method,
        vessel_name: inc.vessel_name ?? '',
        vessel_flag: inc.vessel_flag ?? '',
        description: inc.description,
        interdicting_forces: inc.interdicting_forces ?? '',
        sources: (inc.sources ?? []).join(', '),
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [inc.lng, inc.lat],
      },
    })),
  };
}

function buildWaypointsGeoJson(routes: CorridorRoute[]): GeoJSON.FeatureCollection {
  // Deduplicate waypoints by name
  const seen = new Set<string>();
  const features: GeoJSON.Feature[] = [];

  for (const route of routes) {
    for (const wp of route.waypoints) {
      if (seen.has(wp.name)) continue;
      seen.add(wp.name);
      features.push({
        type: 'Feature',
        properties: {
          name: wp.name,
          type: wp.type,
        },
        geometry: {
          type: 'Point',
          coordinates: [wp.lng, wp.lat],
        },
      });
    }
  }

  return { type: 'FeatureCollection', features };
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation state
// ─────────────────────────────────────────────────────────────────────────────

const DASH_LENGTH = 2;
const GAP_LENGTH = 4;
const TOTAL_LENGTH = DASH_LENGTH + GAP_LENGTH; // 6

let routeAnimationId: number | null = null;
let dashOffset = 0;

let waypointPulseIntervalId: ReturnType<typeof setInterval> | null = null;
let waypointPulseExpanded = false;

// ─────────────────────────────────────────────────────────────────────────────
// Route flow animation
// ─────────────────────────────────────────────────────────────────────────────

export function startRouteAnimation(map: mapboxgl.Map): void {
  if (routeAnimationId !== null) return;

  const animate = () => {
    dashOffset += 0.15;
    if (dashOffset >= TOTAL_LENGTH) dashOffset -= TOTAL_LENGTH;

    // Compute the phase-shifted dasharray
    // The idea: at offset 0, pattern is [DASH, GAP]. As offset grows,
    // we eat into the dash portion, then into the gap portion.
    const phase = dashOffset % TOTAL_LENGTH;
    let dashArray: number[];

    if (phase <= DASH_LENGTH) {
      // We are within the dash portion
      const remainingDash = DASH_LENGTH - phase;
      dashArray = [remainingDash, GAP_LENGTH, phase, 0];
    } else {
      // We are within the gap portion
      const intoGap = phase - DASH_LENGTH;
      const remainingGap = GAP_LENGTH - intoGap;
      dashArray = [0, remainingGap, DASH_LENGTH, intoGap];
    }

    // Filter out zero-length segments for cleaner rendering
    const cleanArray = dashArray.filter((v) => v > 0);
    // Mapbox needs at least 2 elements
    const finalArray = cleanArray.length >= 2 ? cleanArray : [DASH_LENGTH, GAP_LENGTH];

    for (const layerId of [ROUTE_NORTHERN_LAYER, ROUTE_CENTRAL_LAYER, ROUTE_SOUTHERN_LAYER]) {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'line-dasharray', finalArray);
      }
    }

    routeAnimationId = requestAnimationFrame(animate);
  };

  routeAnimationId = requestAnimationFrame(animate);
}

export function stopRouteAnimation(): void {
  if (routeAnimationId !== null) {
    cancelAnimationFrame(routeAnimationId);
    routeAnimationId = null;
  }
  dashOffset = 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Waypoint pulse animation
// ─────────────────────────────────────────────────────────────────────────────

export function startWaypointPulse(map: mapboxgl.Map): void {
  if (waypointPulseIntervalId !== null) return;

  waypointPulseIntervalId = setInterval(() => {
    waypointPulseExpanded = !waypointPulseExpanded;

    if (map.getLayer(WAYPOINTS_PULSE_LAYER)) {
      map.setPaintProperty(
        WAYPOINTS_PULSE_LAYER,
        'circle-radius',
        waypointPulseExpanded ? 22 : 12,
      );
      map.setPaintProperty(
        WAYPOINTS_PULSE_LAYER,
        'circle-opacity',
        waypointPulseExpanded ? 0.15 : 0.35,
      );
    }
  }, 1500);
}

export function stopWaypointPulse(): void {
  if (waypointPulseIntervalId !== null) {
    clearInterval(waypointPulseIntervalId);
    waypointPulseIntervalId = null;
  }
  waypointPulseExpanded = false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Visibility helpers
// ─────────────────────────────────────────────────────────────────────────────

export function setCorridorLayerVisibility(
  map: mapboxgl.Map,
  layerIds: string[],
  visible: boolean,
): void {
  const visibility = visible ? 'visible' : 'none';
  for (const layerId of layerIds) {
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  }
}

export function setSeizureDateFilter(
  map: mapboxgl.Map,
  startDate: string,
  endDate: string,
): void {
  if (map.getLayer(SEIZURES_LAYER)) {
    map.setFilter(SEIZURES_LAYER, [
      'all',
      ['>=', ['get', 'date'], startDate],
      ['<=', ['get', 'date'], endDate],
    ]);
  }
}

export function clearSeizureDateFilter(map: mapboxgl.Map): void {
  if (map.getLayer(SEIZURES_LAYER)) {
    map.setFilter(SEIZURES_LAYER, null);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Popup helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatKg(kg: number): string {
  return kg >= 1000
    ? `${(kg / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })} tonnes`
    : `${kg.toLocaleString('en-US')} kg`;
}

function formatDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

function formatMethodLabel(method: string): string {
  return METHOD_LABELS[method as SeizureMethod] ?? method;
}

function setupSeizurePopups(map: mapboxgl.Map): void {
  const popup = new mapboxgl.Popup({ closeButton: false, offset: 16, maxWidth: '360px' });

  const handleMouseEnter = (e: mapboxgl.MapLayerMouseEvent) => {
    map.getCanvas().style.cursor = 'pointer';
    const properties = e.features?.[0]?.properties;
    if (!properties) return;

    const method = properties.method as string;
    const seizureKg = Number(properties.seizure_kg) || 0;
    const vesselName = properties.vessel_name as string;
    const vesselFlag = properties.vessel_flag as string;

    const fields: MapPopupField[] = [
      { label: 'Seizure', value: formatKg(seizureKg) },
      { label: 'Date', value: formatDate(properties.date as string) },
      { label: 'Method', value: formatMethodLabel(method) },
    ];

    const interdictingForces = properties.interdicting_forces as string;
    if (interdictingForces) {
      fields.push({ label: 'Interdicting Forces', value: interdictingForces });
    }

    const description = properties.description as string;
    if (description) {
      fields.push({
        label: 'Description',
        value: description,
        valueClassName: 'text-xs',
      });
    }

    // Build subtitle from vessel name + flag
    let subtitle: string | undefined;
    if (vesselName && vesselFlag) {
      subtitle = `${vesselName} (${vesselFlag})`;
    } else if (vesselName) {
      subtitle = vesselName;
    }

    const popupProps: MapPopupProps = {
      badge: { label: formatMethodLabel(method), variant: 'danger' },
      title: properties.operation as string,
      subtitle,
      sections: [{ id: 'seizure-details', fields }],
    };

    setMapPopupContent(popup, e.lngLat, popupProps);
    popup.addTo(map);
  };

  const handleMouseMove = (e: mapboxgl.MapLayerMouseEvent) => {
    popup.setLngLat(e.lngLat);
  };

  const handleMouseLeave = () => {
    map.getCanvas().style.cursor = '';
    popup.remove();
  };

  map.on('mouseenter', SEIZURES_LAYER, handleMouseEnter);
  map.on('mousemove', SEIZURES_LAYER, handleMouseMove);
  map.on('mouseleave', SEIZURES_LAYER, handleMouseLeave);
}

// ─────────────────────────────────────────────────────────────────────────────
// Layer creation helpers
// ─────────────────────────────────────────────────────────────────────────────

function addHeatmapLayer(map: mapboxgl.Map, seizuresGeoJson: GeoJSON.FeatureCollection): void {
  addSourceDirect(map, HEATMAP_SOURCE, seizuresGeoJson);
  addLayerDirect(map, {
    id: HEATMAP_LAYER,
    type: 'heatmap',
    source: HEATMAP_SOURCE,
    paint: {
      'heatmap-weight': [
        'interpolate', ['linear'], ['get', 'seizure_kg'],
        0, 0,
        2000, 0.4,
        5000, 0.7,
        10000, 1,
      ],
      'heatmap-intensity': [
        'interpolate', ['linear'], ['zoom'],
        0, 1,
        3, 2,
        5, 3,
      ],
      'heatmap-radius': [
        'interpolate', ['linear'], ['zoom'],
        0, 30,
        3, 50,
        5, 70,
        10, 90,
      ],
      'heatmap-color': [
        'interpolate', ['linear'], ['heatmap-density'],
        0, 'rgba(0,0,0,0)',
        0.1, 'rgba(178,24,43,0.4)',
        0.3, 'rgba(214,96,77,0.6)',
        0.5, 'rgba(244,109,67,0.75)',
        0.7, 'rgba(253,174,97,0.85)',
        0.9, 'rgba(254,224,139,0.9)',
        1.0, 'rgba(255,255,191,0.95)',
      ],
      'heatmap-opacity': [
        'interpolate', ['linear'], ['zoom'],
        0, 0.9,
        5, 0.8,
        10, 0.5,
      ],
    } as unknown as mapboxgl.HeatmapPaint,
    layout: { visibility: 'none' },
  } as mapboxgl.AnyLayer);
}

function addRouteLayers(map: mapboxgl.Map, routesGeoJson: GeoJSON.FeatureCollection): void {
  const routeLineWidth = [
    'interpolate', ['linear'], ['zoom'],
    1, 2,
    5, 4,
    10, 6,
  ];

  // Create shared source for all 3 route layers
  addSourceDirect(map, ROUTES_SOURCE, routesGeoJson);

  // Northern route
  addLayerDirect(map, {
    id: ROUTE_NORTHERN_LAYER,
    type: 'line',
    source: ROUTES_SOURCE,
    filter: ['==', ['get', 'type'], 'northern'],
    paint: {
      'line-color': ROUTE_COLORS.northern,
      'line-width': routeLineWidth,
      'line-opacity': 0.85,
      'line-dasharray': [DASH_LENGTH, GAP_LENGTH],
    } as unknown as mapboxgl.LinePaint,
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
      visibility: 'none',
    },
  } as mapboxgl.AnyLayer);

  // Central route
  addLayerDirect(map, {
    id: ROUTE_CENTRAL_LAYER,
    type: 'line',
    source: ROUTES_SOURCE,
    filter: ['==', ['get', 'type'], 'central'],
    paint: {
      'line-color': ROUTE_COLORS.central,
      'line-width': routeLineWidth,
      'line-opacity': 0.85,
      'line-dasharray': [DASH_LENGTH, GAP_LENGTH],
    } as unknown as mapboxgl.LinePaint,
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
      visibility: 'none',
    },
  } as mapboxgl.AnyLayer);

  // Southern route
  addLayerDirect(map, {
    id: ROUTE_SOUTHERN_LAYER,
    type: 'line',
    source: ROUTES_SOURCE,
    filter: ['==', ['get', 'type'], 'southern'],
    paint: {
      'line-color': ROUTE_COLORS.southern,
      'line-width': routeLineWidth,
      'line-opacity': 0.85,
      'line-dasharray': [DASH_LENGTH, GAP_LENGTH],
    } as unknown as mapboxgl.LinePaint,
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
      visibility: 'none',
    },
  } as mapboxgl.AnyLayer);
}

function addVesselTrackLayer(map: mapboxgl.Map): void {
  const vesselTrack = getVesselTrack();
  addSourceDirect(map, VESSEL_TRACK_SOURCE, vesselTrack.geojson as GeoJSON.Feature);
  addLayerDirect(map, {
    id: VESSEL_TRACK_LAYER,
    type: 'line',
    source: VESSEL_TRACK_SOURCE,
    paint: {
      'line-color': '#FFFFFF',
      'line-width': [
        'interpolate', ['linear'], ['zoom'],
        1, 1.5,
        5, 3,
        10, 4,
      ],
      'line-opacity': 0.7,
      'line-dasharray': [3, 3],
    } as unknown as mapboxgl.LinePaint,
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
      visibility: 'none',
    },
  } as mapboxgl.AnyLayer);
}

function addSeizureLayers(map: mapboxgl.Map, seizuresGeoJson: GeoJSON.FeatureCollection): void {
  addSourceDirect(map, SEIZURES_SOURCE, seizuresGeoJson);
  addLayerDirect(map, {
    id: SEIZURES_LAYER,
    type: 'circle',
    source: SEIZURES_SOURCE,
    paint: {
      'circle-radius': [
        'interpolate', ['linear'], ['get', 'seizure_kg'],
        200, 6,
        1000, 10,
        3000, 16,
        5000, 22,
        10000, 30,
      ],
      'circle-color': [
        'match', ['get', 'method'],
        'fishing_vessel', METHOD_COLORS.fishing_vessel,
        'container', METHOD_COLORS.container,
        'go_fast', METHOD_COLORS.go_fast,
        'narco_sub', METHOD_COLORS.narco_sub,
        'sailboat', METHOD_COLORS.sailboat,
        METHOD_COLORS.other,
      ],
      'circle-opacity': 0.9,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-opacity': 0.8,
    } as mapboxgl.CirclePaint,
    layout: { visibility: 'none' },
  } as mapboxgl.AnyLayer);
}

function addWaypointLayers(map: mapboxgl.Map, waypointsGeoJson: GeoJSON.FeatureCollection): void {
  // Create shared source
  addSourceDirect(map, WAYPOINTS_SOURCE, waypointsGeoJson);

  // Pulse outer layer (renders behind the solid inner)
  addLayerDirect(map, {
    id: WAYPOINTS_PULSE_LAYER,
    type: 'circle',
    source: WAYPOINTS_SOURCE,
    paint: {
      'circle-radius': 16,
      'circle-color': '#FFD93D',
      'circle-opacity': 0.3,
      'circle-stroke-width': 0,
    } as mapboxgl.CirclePaint,
    layout: { visibility: 'none' },
  } as mapboxgl.AnyLayer);

  // Solid inner layer
  addLayerDirect(map, {
    id: WAYPOINTS_LAYER,
    type: 'circle',
    source: WAYPOINTS_SOURCE,
    paint: {
      'circle-radius': 8,
      'circle-color': '#FFFFFF',
      'circle-opacity': 0.95,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#FFD93D',
    } as mapboxgl.CirclePaint,
    layout: { visibility: 'none' },
  } as mapboxgl.AnyLayer);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main init function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Add all corridor layers to the map.
 * Safe to call multiple times; uses addSourceDirect/addLayerDirect for idempotent creation.
 */
export async function addCorridorLayer(map: mapboxgl.Map | null | undefined): Promise<void> {
  if (!map) return;

  try {
    const styleReady = await waitForStyleLoaded(map);
    if (!styleReady) {
      console.warn('Corridor: style not ready, skipping');
      return;
    }

    // Fetch data in parallel
    const [incidents, routes] = await Promise.all([
      fetchCorridorIncidents(),
      fetchCorridorRoutes(),
    ]);
    const vesselTrack = getVesselTrack();

    // Build GeoJSON feature collections
    const routesGeoJson = buildRoutesGeoJson(routes);
    const seizuresGeoJson = buildSeizuresGeoJson(incidents);
    const waypointsGeoJson = buildWaypointsGeoJson(routes);

    // Create layers in order: heatmap (bottom) -> routes -> vessel track -> seizures -> waypoint pulse -> waypoints (top)
    addHeatmapLayer(map, seizuresGeoJson);
    addRouteLayers(map, routesGeoJson);
    addVesselTrackLayer(map);
    addSeizureLayers(map, seizuresGeoJson);
    addWaypointLayers(map, waypointsGeoJson);

    // Set up seizure popups
    setupSeizurePopups(map);

    // Trigger repaint to ensure all layers render
    map.triggerRepaint();
  } catch (error) {
    console.error('Corridor: Failed to initialize corridor layers:', error);
  }
}
